/**
 * Tunnel-health controller — bridges the `GatewayBrowserClient` tunnel
 * events to a `TunnelManager` instance and to the host's reactive
 * state. Owns the heartbeat tick (every 5s) that flips
 * `healthy` → `degraded` → `dead` based on staleness.
 *
 * Design contract (matches the active task's acceptance criteria):
 *
 * 1. **Isolated per (sessionId, hostId).** Every operation keys on
 *    `(host.sessionKey, hostId)`. Switching sessions does NOT cross
 *    state — the controller re-arms for the new key.
 * 2. **No composer pollution.** On any tunnel transition, this module
 *    MUST NOT set `host.lastError`. `host.lastError` is reserved for
 *    chat/run errors the operator needs to act on inline. The unit
 *    test `tunnel-health.no-composer-write.test.ts` enforces this.
 * 3. **Reconnect is a button.** When the tunnel is `dead`, the
 *    manager stays sticky until the operator clicks "Reconnect" in
 *    the inspector (or the controller schedules the next auto-retry
 *    within the backoff budget, see {@link scheduleNextAutoReconnect}).
 * 4. **Dead ⇒ cancel work.** On entering `dead`, this controller
 *    fires `chat.abort` for the affected session AND marks every
 *    cancellable subagent card so the UI no longer renders them as
 *    `running`. The actual server-side cleanup is the existing abort
 *    path; the UI side just stops lying.
 */

import {
  hostIdForGatewayUrl,
  type TunnelManager,
  type TunnelRecord,
} from "../tunnel/tunnel-state.ts";
import { cancellableChildSessionKeys } from "./subagents.ts";

/** Tick interval for the heartbeat. 5s is responsive without burning CPU. */
export const TUNNEL_HEALTH_TICK_MS = 5_000;

export type TunnelHealthHost = {
  /** Active session key. The controller arms a record for this key. */
  sessionKey: string;
  /** WebSocket URL the gateway client is connecting to. */
  settings: { gatewayUrl: string };
  /** Optional test seam — the host's GatewayBrowserClient.start() and request() are also a seam. */
  client: {
    start: () => void;
    stop: () => void;
    request: <T>(method: string, params: unknown) => Promise<T>;
  } | null;
  /** Mutable app fields touched by this controller. */
  lastError: string | null;
  /**
   * Per-session tunnel state map. Keyed by hostId (so the same
   * session on two hosts produces two records). The inspector
   * subscribes to this and re-renders on any mutation.
   */
  tunnelRecords: Record<string, TunnelRecord>;
  /**
   * Listener for tunnel state changes. NOT a `host` method — the
   * inspector installs it as a Lit `@state()` updater or as a
   * callback into the inspector panel.
   */
  onTunnelRecordsChanged?: () => void;
  /**
   * Optional callback fired when a tunnel enters `dead` so the host
   * can run side effects (e.g. `chat.abort`, mark subagent cards).
   * The host wires this in `app.ts` so this controller stays free
   * of Lit-element concerns.
   */
  onTunnelDead?: (key: { sessionId: string; hostId: string }) => void;
  /**
   * Subagent cards for the active session. The controller reads
   * `chatSubagents` to find cancellable children and to mark
   * non-running ones. Optional so the controller can be unit-tested
   * without a real app.
   */
  chatSubagents?: import("../ui-types.ts").ChatSubagentCard[];
  /**
   * Mutable list — the controller will set card.status = "cancelled"
   * on every cancellable child. Caller should reassign
   * `host.chatSubagents = [...cards]` to trigger Lit reactivity.
   */
  setChatSubagents?: (cards: import("../ui-types.ts").ChatSubagentCard[]) => void;
  /** Optional logger. */
  log?: (msg: string) => void;
};

export type TunnelHealthController = {
  manager: TunnelManager;
  /**
   * Bind to a host. Sets up the gateway-client `onTunnelEvent` and
   * `onTunnelHeartbeat` callbacks, and arms the heartbeat tick.
   * Idempotent — calling twice with the same host is a no-op.
   */
  bind: (host: TunnelHealthHost) => void;
  /**
   * Operator-initiated reconnect. Drops the sticky `dead` flag,
   * marks the tunnel `connecting`, and asks the gateway client to
   * reconnect. Returns the scheduled delay (ms).
   */
  reconnect: (host: TunnelHealthHost, key: { sessionId: string; hostId: string }) => number;
  /** Stop the heartbeat tick. Called from `disconnectedCallback`. */
  dispose: () => void;
};

/**
 * Build a controller. The manager is shared between calls so a
 * single OpenClawApp instance has one source of truth.
 */
export function createTunnelHealthController(manager: TunnelManager): TunnelHealthController {
  let tickTimer: ReturnType<typeof setInterval> | null = null;
  let boundHost: TunnelHealthHost | null = null;

  const repaint = () => {
    if (!boundHost) {
      return;
    }
    const next: Record<string, TunnelRecord> = {};
    for (const rec of manager.list()) {
      // Only surface records for the active session. Records for
      // background sessions are kept in the manager (so a switch
      // back doesn't lose state) but the inspector only shows the
      // active one.
      if (rec.key.sessionId === boundHost.sessionKey) {
        next[rec.key.hostId] = rec;
      }
    }
    boundHost.tunnelRecords = next;
    boundHost.onTunnelRecordsChanged?.();
  };

  const onEvent = (evt: { state: TunnelRecord["state"]; code?: number; reason?: string }) => {
    if (!boundHost) {
      return;
    }
    const hostId = hostIdForGatewayUrl(boundHost.settings.gatewayUrl);
    const sessionId = boundHost.sessionKey;
    const key = { sessionId, hostId };
    let rec: TunnelRecord;
    if (evt.state === "connecting") {
      rec = manager.markConnecting(key);
    } else if (evt.state === "healthy") {
      rec = manager.markHealthy(key);
    } else if (evt.state === "degraded") {
      rec = manager.markDegraded(key, evt.reason);
    } else {
      rec = manager.markDead(key, `${evt.code ?? ""} ${evt.reason ?? ""}`.trim() || undefined);
      if (rec.deadSticky) {
        boundHost.onTunnelDead?.(key);
      }
    }
    boundHost.log?.(`tunnel ${sessionId}@${hostId} → ${rec.state}`);
    repaint();
  };

  const onHeartbeat = () => {
    if (!boundHost) {
      return;
    }
    const hostId = hostIdForGatewayUrl(boundHost.settings.gatewayUrl);
    manager.heartbeat({ sessionId: boundHost.sessionKey, hostId });
    repaint();
  };

  const onTick = () => {
    const changed = manager.tick();
    if (changed.length === 0) {
      return;
    }
    if (!boundHost) {
      return;
    }
    for (const rec of changed) {
      if (rec.state === "dead" && rec.deadSticky && rec.key.sessionId === boundHost.sessionKey) {
        boundHost.onTunnelDead?.(rec.key);
      }
    }
    repaint();
  };

  return {
    manager,
    bind(host: TunnelHealthHost) {
      if (boundHost === host) {
        return;
      }
      boundHost = host;
      if (tickTimer) {
        clearInterval(tickTimer);
      }
      tickTimer = setInterval(onTick, TUNNEL_HEALTH_TICK_MS);
      // Stash the callbacks on the host so `app.ts` can wire them
      // into the gateway client. The host is responsible for
      // forwarding (it owns the client options); we just publish the
      // shape. The key is intentionally not a `@state()` field — it's
      // an internal channel between the controller and the host
      // element, never user-visible.
      (
        host as unknown as {
          tunnelCallbacks?: { onEvent: typeof onEvent; onHeartbeat: typeof onHeartbeat };
        }
      ).tunnelCallbacks = { onEvent, onHeartbeat };
      repaint();
    },
    reconnect(host, key) {
      const { record, delayMs } = manager.requestReconnect(key);
      host.log?.(`tunnel reconnect requested for ${key.sessionId}@${key.hostId}`);
      // Tell the gateway client to actually try again. The client
      // will emit its own `connecting` event when the socket starts
      // opening.
      try {
        host.client?.stop();
      } catch {
        // best effort
      }
      try {
        host.client?.start();
      } catch (err) {
        // If the client can't be restarted (e.g. stopped permanently),
        // flip back to dead so the inspector shows the right state.
        manager.markDead(key, String(err));
        repaint();
      }
      // The markConnecting from the client's `start()` may not have
      // arrived yet; ensure the record is at least `connecting` now
      // so the chip reflects the in-flight state.
      if (record.state === "connecting") {
        manager.markConnecting(key);
      }
      repaint();
      return delayMs;
    },
    dispose() {
      if (tickTimer) {
        clearInterval(tickTimer);
        tickTimer = null;
      }
      boundHost = null;
    },
  };
}

/**
 * Cancel all work units for a (sessionId, hostId) when its tunnel
 * dies. Called by the controller via `host.onTunnelDead`. Best-effort
 * — failures are logged but never rethrown, so a dead tunnel never
 * takes the UI down with it.
 */
export async function cancelWorkUnitsForDeadTunnel(
  host: TunnelHealthHost,
  key: { sessionId: string; hostId: string },
): Promise<void> {
  if (!host.client) {
    return;
  }
  // 1) Mark every cancellable subagent card so the UI no longer
  // shows them as RUNNING. The actual registry update happens via
  // the next `subagents.list` poll after the abort lands.
  const cards = host.chatSubagents ?? [];
  if (cards.length > 0 && host.setChatSubagents) {
    const cancellable = new Set(cancellableChildSessionKeys(cards));
    if (cancellable.size > 0) {
      const next = cards.map((c) =>
        cancellable.has(c.childSessionKey)
          ? { ...c, status: "cancelled" as const, endedAt: Date.now() }
          : c,
      );
      host.setChatSubagents(next);
    }
  }
  // 2) Issue a `chat.abort` for the parent run. This is the same
  // path the operator takes when they hit "Stop" — keeps the
  // server-side and client-side state coherent.
  try {
    await host.client.request("chat.abort", { sessionKey: key.sessionId });
  } catch (err) {
    host.log?.(`tunnel-dead abort failed for ${key.sessionId}: ${String(err)}`);
  }
}

/**
 * Read the callbacks the controller installed on the host. Used by
 * `app-gateway.ts` to forward to the gateway client. Exported for
 * tests.
 */
export function tunnelCallbacksFor(
  host: TunnelHealthHost,
): {
  onEvent: (evt: { state: TunnelRecord["state"]; code?: number; reason?: string }) => void;
  onHeartbeat: () => void;
} | null {
  const stored = (
    host as unknown as {
      tunnelCallbacks?: {
        onEvent: (evt: { state: TunnelRecord["state"]; code?: number; reason?: string }) => void;
        onHeartbeat: () => void;
      };
    }
  ).tunnelCallbacks;
  return stored ?? null;
}

/**
 * Re-export for callers that need a typed reference. Avoids
 * `app.ts` having to know about the manager's full surface.
 */
export type { TunnelManager } from "../tunnel/tunnel-state.ts";
